import * as React from "react"
import { ChevronDown, Check, Search } from "lucide-react"
import { cn } from "../../lib/utils"
import { US_STATES } from "../../utils/usStates"

interface StateSelectProps {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  className?: string
  error?: boolean
}

export function StateSelect({ 
  value, 
  onValueChange, 
  placeholder = "Select state...", 
  className,
  error = false 
}: StateSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (
        triggerRef.current && 
        !triggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
        setSearchQuery("")
      }
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [open])

  // Focus search input when dropdown opens
  React.useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100)
    }
  }, [open])

  // Find selected state
  const selectedState = US_STATES.find(state => state.abbreviation === value)

  // Filter states based on search query
  const filteredStates = US_STATES.filter(state =>
    state.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    state.abbreviation.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleOptionClick = (stateAbbr: string) => {
    onValueChange(stateAbbr)
    setOpen(false)
    setSearchQuery("")
  }

  const handleTriggerClick = () => {
    setOpen((v) => !v)
    if (!open) {
      setSearchQuery("")
    }
  }

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error ? "border-red-500" : "border-slate-200"
        )}
        onClick={handleTriggerClick}
      >
        <span className="block truncate">
          {selectedState ? `${selectedState.abbreviation} - ${selectedState.name}` : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
      
      {open && (
        <div 
          ref={dropdownRef} 
          className="absolute top-full left-0 z-[9999] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-1"
              />
            </div>
          </div>

          {/* States List */}
          <div className="max-h-40 overflow-y-auto">
            {filteredStates.length > 0 ? (
              filteredStates.map((state) => {
                const isSelected = state.abbreviation === value
                return (
                  <div
                    key={state.abbreviation}
                    onClick={() => handleOptionClick(state.abbreviation)}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center py-2 px-3 text-sm outline-none hover:bg-slate-100 focus:bg-slate-100",
                      isSelected && "bg-slate-100 font-semibold"
                    )}
                  >
                    <span className="font-medium text-slate-900">{state.abbreviation}</span>
                    <span className="ml-2 text-slate-600">- {state.name}</span>
                    {isSelected && <Check className="absolute right-3 h-4 w-4" />}
                  </div>
                )
              })
            ) : (
              <div className="py-2 px-3 text-sm text-slate-500">
                No states found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
} 